import { Link } from 'react-router-dom';
import { useState } from 'react';
import { RefreshCw, AlertTriangle, CreditCard, TrendingDown, Users } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useBalanceAgee } from '@/hooks/useBetonSprint4';
import { formatFCFA } from '@/utils/currency';

const BUCKET_LABELS = {
  a_jour:  'À jour',
  j_0_30:  '0-30 jours',
  j_30_60: '30-60 jours',
  j_60_90: '60-90 jours',
  plus_90: '+90 jours',
} as const;

const BUCKET_COLORS = {
  a_jour:  'var(--success)',
  j_0_30:  'var(--info)',
  j_30_60: 'var(--warning)',
  j_60_90: '#f97316',  // orange
  plus_90: 'var(--danger)',
} as const;

export const BalanceAgeePage = () => {
  const [view, setView] = useState<'clients' | 'factures'>('clients');
  const { data, isLoading, refetch } = useBalanceAgee();

  return (
    <>
      <Topbar
        title="Balance âgée clients"
        subtitle="Module 10 — Créances par tranches d'âge (0-30j / 30-60j / 60-90j / +90j) — relance recouvrement"
        actions={
          <button
            className="ds-btn ds-btn-p ds-btn-sm"
            onClick={() => void refetch()}
            title="Régénère le calcul à la date du jour (ticket SIMGROUP-2026-002 M-06)"
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /> Recalculer
          </button>
        }
      />
      <div className="ds-content">
        {!data && isLoading && <div style={{ padding: 32, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>}

        {data && (
          <>
            {/* KPI strip 5 buckets */}
            <div className="ds-kpi-strip">
              {(Object.keys(BUCKET_LABELS) as Array<keyof typeof BUCKET_LABELS>).map((b) => {
                const cents = BigInt(data.buckets[b].encoursCents);
                const ks = b === 'a_jour' ? 'ks-green'
                         : b === 'j_0_30' ? 'ks-blue'
                         : b === 'j_30_60' ? 'ks-orange'
                         : b === 'j_60_90' ? 'ks-orange'
                         : 'ks-red';
                return (
                  <div key={b} className={`ds-ks ${ks}`}>
                    <div className="ds-ks-label">{BUCKET_LABELS[b]}</div>
                    <div className="ds-ks-value" style={{ fontSize: 17 }}>{formatFCFA(cents)}</div>
                    <div className="ds-ks-sub">{data.buckets[b].count} facture{data.buckets[b].count > 1 ? 's' : ''}</div>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 6, background: 'var(--surface)', border: '1px solid var(--bdr)', marginBottom: 14 }}>
              <div style={{ fontSize: 12 }}>
                <TrendingDown size={12} style={{ display: 'inline', marginRight: 4 }} />
                <strong>Encours total : {formatFCFA(BigInt(data.total.encoursCents))}</strong> sur {data.total.nbFactures} facture{data.total.nbFactures > 1 ? 's' : ''}
                <span style={{ color: 'var(--tx-3)', marginLeft: 6 }}>
                  · calcul du {data.asOf}{' '}
                  <button
                    onClick={() => void refetch()}
                    title="Régénérer le calcul à la date du jour"
                    style={{ background: 'none', border: 'none', color: 'var(--p)', cursor: 'pointer', padding: 0, fontSize: 11, textDecoration: 'underline' }}
                  >
                    (recalculer)
                  </button>
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className={`ds-btn ds-btn-sm ${view === 'clients' ? 'ds-btn-p' : 'ds-btn-g'}`} onClick={() => setView('clients')}>
                  <Users size={12} /> Par client
                </button>
                <button className={`ds-btn ds-btn-sm ${view === 'factures' ? 'ds-btn-p' : 'ds-btn-g'}`} onClick={() => setView('factures')}>
                  <CreditCard size={12} /> Par facture
                </button>
              </div>
            </div>

            {view === 'clients' && (
              <div className="ds-table-panel">
                <div className="ds-table-header">
                  <div>
                    <div className="ds-table-title">Encours par client</div>
                    <div className="ds-table-sub">{data.parClient.length} client{data.parClient.length > 1 ? 's' : ''} actif{data.parClient.length > 1 ? 's' : ''}</div>
                  </div>
                </div>
                <table className="ds-tbl">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th className="ds-am">À jour</th>
                      <th className="ds-am">0-30 j</th>
                      <th className="ds-am">30-60 j</th>
                      <th className="ds-am">60-90 j</th>
                      <th className="ds-am">+90 j</th>
                      <th className="ds-am" style={{ background: 'rgba(0,127,71,0.05)' }}>Total</th>
                      <th className="ds-am" style={{ width: 80 }}>Nb fact.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.parClient.map((c) => (
                      <tr key={c.contactId}>
                        <td><Link to={`/finance/releves/${c.contactId}`} title="Relevé de compte" style={{ color: 'var(--p)', textDecoration: 'underline' }}>{c.nom}</Link></td>
                        <td className="ds-am" style={{ color: 'var(--success)' }}>{BigInt(c.a_jour) > BigInt(0) ? formatFCFA(BigInt(c.a_jour)) : '—'}</td>
                        <td className="ds-am">{BigInt(c.j_0_30) > BigInt(0) ? formatFCFA(BigInt(c.j_0_30)) : '—'}</td>
                        <td className="ds-am" style={{ color: 'var(--warning)' }}>{BigInt(c.j_30_60) > BigInt(0) ? formatFCFA(BigInt(c.j_30_60)) : '—'}</td>
                        <td className="ds-am" style={{ color: '#f97316' }}>{BigInt(c.j_60_90) > BigInt(0) ? formatFCFA(BigInt(c.j_60_90)) : '—'}</td>
                        <td className="ds-am" style={{ color: 'var(--danger)', fontWeight: 600 }}>
                          {BigInt(c.plus_90) > BigInt(0) && <AlertTriangle size={10} style={{ display: 'inline', marginRight: 3 }} />}
                          {BigInt(c.plus_90) > BigInt(0) ? formatFCFA(BigInt(c.plus_90)) : '—'}
                        </td>
                        <td className="ds-am" style={{ fontWeight: 700, background: 'rgba(0,127,71,0.05)' }}>{formatFCFA(BigInt(c.total))}</td>
                        <td className="ds-am" style={{ fontSize: 11 }}>{c.nbFactures}</td>
                      </tr>
                    ))}
                    {data.parClient.length === 0 && (
                      <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>
                        Aucun encours client (toutes factures soldées)
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {view === 'factures' && (
              <div className="ds-table-panel">
                <div className="ds-table-header">
                  <div>
                    <div className="ds-table-title">Détail factures impayées</div>
                    <div className="ds-table-sub">{data.factures.length} facture{data.factures.length > 1 ? 's' : ''} · triées par ancienneté</div>
                  </div>
                </div>
                <table className="ds-tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 130 }}>Référence</th>
                      <th>Client</th>
                      <th style={{ width: 110 }}>Échéance</th>
                      <th className="ds-am" style={{ width: 80 }}>Retard</th>
                      <th style={{ width: 110 }}>Tranche</th>
                      <th className="ds-am" style={{ width: 130 }}>Encours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.factures.map((f) => (
                      <tr key={f.id}>
                        <td><span className="ds-ref-code">{f.reference}</span></td>
                        <td>{f.contact.companyName ?? f.contact.fullName}</td>
                        <td style={{ fontSize: 11, color: 'var(--tx-2)' }}>{f.dueDate}</td>
                        <td className="ds-am" style={{ fontSize: 11, color: f.daysOverdue > 0 ? 'var(--danger)' : 'var(--success)' }}>
                          {f.daysOverdue > 0 ? `+${f.daysOverdue}j` : `${f.daysOverdue}j`}
                        </td>
                        <td>
                          <span className="ds-bd" style={{ background: BUCKET_COLORS[f.bucket], color: 'white', fontSize: 10 }}>
                            {BUCKET_LABELS[f.bucket]}
                          </span>
                        </td>
                        <td className="ds-am" style={{ fontWeight: 600 }}>{formatFCFA(BigInt(f.encoursCents))}</td>
                      </tr>
                    ))}
                    {data.factures.length === 0 && (
                      <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>
                        Aucune facture impayée
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
